import React from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, X, HelpCircle } from 'lucide-react';

/**
 * Premium Themed Confirmation Modal
 * Used for actions like clearing cart, deleting items, etc.
 */
const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Are you sure?',
    message = 'This action cannot be undone.',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    type = 'caution', // caution, danger, info
    icon: IconComponent = null
}) => {
    if (!isOpen) return null;
    if (typeof document === 'undefined') return null;

    const getTypeStyles = () => {
        switch (type) {
            case 'danger':
                return {
                    icon: <AlertCircle className="w-6 h-6 text-red-600" />,
                    iconBg: 'bg-red-100',
                    confirmBtn: 'bg-red-600 hover:bg-red-700 shadow-red-500/30'
                };
            case 'info':
                return {
                    icon: <HelpCircle className="w-6 h-6 text-cyan-600" />,
                    iconBg: 'bg-cyan-100',
                    confirmBtn: 'bg-cyan-600 hover:bg-cyan-700 shadow-cyan-500/30'
                };
            default: // caution
                return {
                    icon: <AlertCircle className="w-6 h-6 text-yellow-600" />,
                    iconBg: 'bg-yellow-100',
                    confirmBtn: 'bg-cyan-600 hover:bg-cyan-700 shadow-cyan-500/30'
                };
        }
    };

    const styles = getTypeStyles();

    return createPortal(
        <div className="fixed inset-0 h-[100dvh] w-screen bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="relative p-6 pb-0 flex items-start justify-between flex-shrink-0">
                    <div className={`${styles.iconBg} p-3 rounded-2xl shadow-inner`}>
                        {IconComponent ? (
                            React.isValidElement(IconComponent) ? (
                                IconComponent
                            ) : (
                                <IconComponent className="w-6 h-6" />
                            )
                        ) : (
                            styles.icon
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors active:scale-90"
                    >
                        <X className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto min-h-0 flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight">
                        {title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                        {message}
                    </p>
                </div>

                {/* Footer */}
                <div className="p-6 pt-0 flex gap-3 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 px-4 rounded-xl font-bold text-gray-600 bg-gray-50 hover:bg-gray-100 transition-all active:scale-95 border border-gray-100"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                        className={`flex-1 py-3 px-4 rounded-xl font-bold text-white transition-all active:scale-95 shadow-lg ${styles.confirmBtn}`}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>

            <style>{`
                .animate-in {
                    animation-fill-mode: forwards;
                }
                .fade-in {
                    animation: fadeIn 0.2s ease-out;
                }
                .zoom-in-95 {
                    animation: zoomIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes zoomIn {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
            `}</style>
        </div>,
        document.body
    );
};

export default ConfirmationModal;
